import { use, useMemo } from "react";
import { type GQL } from "@binale-tech/shared";
import { CalendarEventsContext } from "@domain/context/CalendarEventsContext";
import { type CalendarEvent } from "./types";

// `eventDate` is an all-day `yyyy-mm-dd` string end to end (no timezone) - parse as a
// local calendar date, never via `new Date(isoString)` (which parses bare `yyyy-mm-dd`
// as UTC midnight and can shift a day off in negative-UTC-offset timezones).
const parseIsoDate = (value: string): Date => {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};

// Pass-through i18n message (`"{title}"`, app.home.calendar.userEventTitle) so the
// existing `EventLabel`/`FormattedMessage` rendering in HomeCalendarWidget works
// unchanged for free-text user titles, same as every derived-deadline titleId.
export const USER_EVENT_TITLE_ID = "app.home.calendar.userEventTitle";

/** Maps the raw GQL.ICalendarEvent[] from CalendarEventsContext into the merged
 * widget CalendarEvent[] shape - source: "user", stateKey = the row id (design spec §14.2). */
export const useUserCalendarEvents = (): CalendarEvent[] => {
    const { events } = use(CalendarEventsContext);

    return useMemo(
        () =>
            events.map((e): CalendarEvent => ({
                id: e.id,
                date: parseIsoDate(e.eventDate),
                category: "user",
                titleId: USER_EVENT_TITLE_ID,
                titleValues: { title: e.title },
                source: "user",
                stateKey: e.id,
                color: (e.color as GQL.IDocumentColors | null) ?? undefined,
            })),
        [events],
    );
};
